import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { useInstructorAuth } from '../../context/InstructorAuthContext';
import './InstructorPortal.css';

const TABS = [
  { id: 'overview',     label: 'Overview'      },
  { id: 'bookings',     label: 'Bookings'      },
  { id: 'profile',      label: 'My Details'    },
  { id: 'documents',    label: 'Documents'     },
  { id: 'testcentres',  label: 'Test Centres'  },
  { id: 'referrals',    label: 'Referrals'     },
  { id: 'security',     label: 'Security'      },
];

const DOC_DEFINITIONS = [
  {
    type: 'driving_licence',
    label: 'Driving Licence (Front)',
    icon: '🪪',
    field: 'driving_licence_file',
    description: 'Front of your UK Driving Licence',
  },
  {
    type: 'driving_licence_back',
    label: 'Driving Licence (Back)',
    icon: '🪪',
    field: 'driving_licence_back_file',
    description: 'Back of your UK Driving Licence',
  },
  {
    type: 'insurance',
    label: 'Vehicle Insurance',
    icon: '🛡️',
    field: 'insurance_certificate_file',
    description: 'Valid motor insurance certificate',
  },
  {
    type: 'mot',
    label: 'MOT Certificate',
    icon: '🔧',
    field: 'mot_certificate_file',
    description: 'Current MOT certificate (if applicable)',
  },
  {
    type: 'green_badge',
    label: 'ADI Green Badge (Front)',
    icon: '✅',
    field: 'green_badge_file',
    description: 'Front of your ADI badge / registration certificate',
  },
  {
    type: 'green_badge_back',
    label: 'ADI Green Badge (Back)',
    icon: '✅',
    field: 'green_badge_back_file',
    description: 'Back of your ADI badge / registration certificate',
  },
];

// ────────────────────────────────────────────────────────────────
const InstructorPortal = () => {
  const navigate = useNavigate();
  const { instructor, logout, refreshProfile } = useInstructorAuth();

  const [activeTab, setActiveTab] = useState('overview');
  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Profile edit state
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [editMsg, setEditMsg] = useState(null);

  // Document upload state
  const [uploadStates, setUploadStates] = useState({});
  const fileInputRefs = useRef({});

  // Referrals state
  const [referrals, setReferrals] = useState(null);
  const [referralStats, setReferralStats] = useState(null);

  // Bookings state
  const [bookings, setBookings] = useState(null);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [completingId, setCompletingId] = useState(null);

  // Security (change password) state
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState(null);

  // Test Centres state
  const [testCentres, setTestCentres] = useState(null);
  const [selectedCentreIds, setSelectedCentreIds] = useState(new Set());
  const [tcSaving, setTcSaving] = useState(false);
  const [tcMsg, setTcMsg] = useState(null);
  const [tcSearch, setTcSearch] = useState('');
  const [tcDeleting, setTcDeleting] = useState(null); // id being deleted

  // ── Load Profile ──────────────────────────────────────────────
  useEffect(() => {
    // Pending instructors don't have a profile record yet — skip fetch
    if (instructor?.status === 'pending' || instructor?.reloginRequired) {
      setLoadingProfile(false);
      return;
    }
    const fetchProfile = async () => {
      try {
        const res = await api.get('/instructor-portal/profile');
        setProfile(res.profile || res);
        const p = res.profile || res;
        setEditForm({
          contact_number: p.contact_number || '',
          address_line1:  p.address_line1  || '',
          address_line2:  p.address_line2  || '',
          city:           p.city           || '',
          postcode:       p.postcode       || '',
        });
      } catch (err) {
        if (err.status === 401) navigate('/instructor-login');
      } finally {
        setLoadingProfile(false);
      }
    };
    fetchProfile();
  }, [navigate, instructor?.status, instructor?.reloginRequired]);

  // ── Load Test Centres when tab is active ────────────────────
  useEffect(() => {
    if (activeTab !== 'testcentres' || testCentres !== null) return;
    const fetchTestCentres = async () => {
      try {
        const res = await api.get('/instructor-portal/test-centres');
        const centres = res.testCentres || [];
        setTestCentres(centres);
        setSelectedCentreIds(new Set(centres.filter(c => c.selected).map(c => c.id)));
      } catch {
        setTestCentres([]);
      }
    };
    fetchTestCentres();
  }, [activeTab, testCentres]);

  // ── Load Referrals when tab is active ─────────────────────────
  useEffect(() => {
    if (activeTab !== 'referrals' || referrals !== null) return;
    const fetchReferrals = async () => {
      try {
        const res = await api.get('/instructor-portal/referrals');
        setReferrals(res.referrals || res);
        setReferralStats(res.stats || null);
      } catch {
        setReferrals([]);
      }
    };
    fetchReferrals();
  }, [activeTab, referrals]);
  // ── Load Bookings when tab is active ──────────────────────────
  useEffect(() => {
    if (activeTab !== 'bookings' || bookings !== null) return;
    const fetchBookings = async () => {
      try {
        setBookingsLoading(true);
        const res = await api.get('/instructor-portal/bookings');
        setBookings(res.bookings || []);
      } catch {
        setBookings([]);
      } finally {
        setBookingsLoading(false);
      }
    };
    fetchBookings();
  }, [activeTab, bookings]);

  // ── Save Test Centre selection ──────────────────────────────────
  const handleSaveTestCentres = async () => {
    setTcSaving(true);
    setTcMsg(null);
    try {
      await api.put('/instructor-portal/test-centres', {
        test_centre_ids: Array.from(selectedCentreIds),
      });
      // Refresh list so the registered section reflects the latest state
      const res = await api.get('/instructor-portal/test-centres');
      const centres = res.testCentres || [];
      setTestCentres(centres);
      setSelectedCentreIds(new Set(centres.filter(c => c.selected).map(c => c.id)));
      setTcMsg({ type: 'success', text: 'Test centres updated successfully.' });
    } catch (err) {
      setTcMsg({ type: 'error', text: err.message || 'Failed to save changes.' });
    } finally {
      setTcSaving(false);
    }
  };

  // ── Remove a single test centre ───────────────────────────────
  const handleRemoveTestCentre = async (centreId) => {
    setTcDeleting(centreId);
    setTcMsg(null);
    try {
      await api.delete(`/instructor-portal/test-centres/${centreId}`);
      // Update local state immediately — no full reload needed
      setTestCentres(prev => prev.map(c => c.id === centreId ? { ...c, selected: false } : c));
      setSelectedCentreIds(prev => { const next = new Set(prev); next.delete(centreId); return next; });
      setTcMsg({ type: 'success', text: 'Test centre removed.' });
    } catch (err) {
      setTcMsg({ type: 'error', text: err.message || 'Failed to remove test centre.' });
    } finally {
      setTcDeleting(null);
    }
  };

  // ── Mark session as completed ──────────────────────────────────
  const handleMarkComplete = async (bookingId) => {
    if (!window.confirm('Mark this session as completed? This cannot be undone.')) return;
    setCompletingId(bookingId);
    try {
      await api.post(`/instructor-portal/bookings/${bookingId}/complete`, {});
      // Refresh bookings list
      setBookings(null);
    } catch {
      alert('Failed to mark session complete. Please try again.');
    } finally {
      setCompletingId(null);
    }
  };
  // ── Logout ────────────────────────────────────────────────────
  const handleLogout = async () => {
    await logout();
    navigate('/instructor-login');
  };

  // ── Save Profile ──────────────────────────────────────────────
  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setEditSaving(true);
    setEditMsg(null);
    try {
      await api.put('/instructor-portal/profile', editForm);
      setEditMsg({ type: 'success', text: 'Details updated successfully.' });
      await refreshProfile();
    } catch (err) {
      setEditMsg({ type: 'error', text: err.message || 'Failed to save changes.' });
    } finally {
      setEditSaving(false);
    }
  };

  // ── Document Upload ───────────────────────────────────────────
  const handleDocUpload = async (docType, file, extraData = {}) => {
    if (!file) return;

    setUploadStates(prev => ({ ...prev, [docType]: { loading: true, error: null, success: null } }));

    const formData = new FormData();
    formData.append('document', file);
    Object.entries(extraData).forEach(([k, v]) => formData.append(k, v));

    try {
      await api.post(`/instructor-portal/documents/${docType}`, formData);
      setUploadStates(prev => ({
        ...prev,
        [docType]: { loading: false, error: null, success: 'Uploaded successfully!' }
      }));
      // Refresh profile to show new file info
      const res = await api.get('/instructor-portal/profile');
      setProfile(res.profile || res);
    } catch (err) {
      setUploadStates(prev => ({
        ...prev,
        [docType]: {
          loading: false,
          error: err.message || 'Upload failed.',
          success: null
        }
      }));
    }
  };

  // ── Change Password ───────────────────────────────────────────
  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwSaving(true);
    setPwMsg(null);
    try {
      await api.post('/instructor-portal/change-password', pwForm);
      setPwMsg({ type: 'success', text: 'Password changed successfully.' });
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      setPwMsg({ type: 'error', text: err.message || 'Failed to change password.' });
    } finally {
      setPwSaving(false);
    }
  };

  // ── Helpers ───────────────────────────────────────────────────
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
  const hasFile = (path) => !!(path && path.trim());

  if (loadingProfile) {
    return (
      <div className="ip-loading">
        <div className="ip-spinner-large" />
        <p>Loading your portal…</p>
      </div>
    );
  }

  // ── PENDING: show application status screen ───────────────────
  if (instructor?.reloginRequired) {
    return (
      <div className="ip-pending-page">
        <div className="ip-pending-card">
          <div className="ip-pending-icon ip-pending-icon--approved"><span>&#10003;</span></div>
          <h1 className="ip-pending-title">Application Approved!</h1>
          <p className="ip-pending-info">
            Your application has been approved. Please sign in again to access your full instructor portal.
          </p>
          <button className="ip-pending-logout" onClick={handleLogout} type="button">
            Sign Out & Log In Again
          </button>
        </div>
      </div>
    );
  }

  // ── NO APPLICATION: prompt instructor to complete the ADI form ─
  if (instructor?.status === 'pending' && instructor?.appStatus === 'no_application') {
    return (
      <div className="ip-pending-page">
        <div className="ip-pending-card">
          <div className="ip-pending-icon" style={{ background: '#dbeafe' }}>
            <span style={{ fontSize: 32 }}>&#128203;</span>
          </div>
          <h1 className="ip-pending-title">Complete Your Application</h1>
          <p className="ip-pending-subtitle">
            Your account is set up, but you haven&apos;t submitted your ADI application yet.
          </p>
          <p className="ip-pending-info">
            Please complete and submit your instructor application to continue.
          </p>
          <button
            className="ip-pending-logout"
            style={{ background: '#003399', marginBottom: 12 }}
            onClick={async () => {
              try {
                const res = await api.post('/instructor-auth/reapply-token');
                const { registrationToken, email } = res;
                sessionStorage.setItem('instructorRegToken', registrationToken);
                sessionStorage.setItem('instructorPreEmail', email);
                navigate('/apply');
              } catch (err) {
                alert('Unable to start application. Please contact support.');
              }
            }}
            type="button"
          >
            Complete Application &#8594;
          </button>
          <p className="ip-pending-contact">
            Questions? Email us at{' '}
            <a href="mailto:contact@fasttrackds.co.uk">contact@fasttrackds.co.uk</a>
          </p>
          <button className="ip-pending-logout" onClick={handleLogout} type="button">
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  // ── PENDING: show application status screen ───────────────────
  if (instructor?.status === 'pending') {
    const appStatusLabel = {
      pending:        'Under Review',
      approved:       'Approved',
      rejected:       'Unsuccessful',
      no_application: 'Not Submitted'
    }[instructor.appStatus] || 'Under Review';

    const appStatusColor = {
      pending:        '#f59e0b',
      approved:       '#10b981',
      rejected:       '#ef4444',
      no_application: '#94a3b8'
    }[instructor.appStatus] || '#f59e0b';

    return (
      <div className="ip-pending-page">
        <div className="ip-pending-card">
          <div className="ip-pending-icon"><span>&#9965;</span></div>
          <h1 className="ip-pending-title">Application Submitted</h1>
          <p className="ip-pending-subtitle">Thank you for applying to join Fast Track Driving School.</p>

          <div className="ip-pending-status-box" style={{ borderColor: appStatusColor }}>
            <span className="ip-pending-status-dot" style={{ background: appStatusColor }} />
            <div>
              <div className="ip-pending-status-label">Application Status</div>
              <div className="ip-pending-status-value" style={{ color: appStatusColor }}>{appStatusLabel}</div>
            </div>
          </div>

          {instructor.appNumber && (
            <div className="ip-pending-ref">
              Reference: <strong>{instructor.appNumber}</strong>
            </div>
          )}

          {instructor.submittedAt && (
            <div className="ip-pending-ref">
              Submitted: <strong>{new Date(instructor.submittedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</strong>
            </div>
          )}

          <p className="ip-pending-info">
            Our team reviews all applications within <strong>2–5 working days</strong>.
            You will receive an email at <strong>{instructor.email}</strong> once a decision has been made.
          </p>

          {instructor.appStatus === 'rejected' && (
            <div className="ip-pending-rejected">
              <p>Unfortunately your application was not successful this time.</p>
              {instructor.rejectionReason && (
                <div className="ip-rejection-reason">
                  <strong>Reason provided:</strong>
                  <p>{instructor.rejectionReason}</p>
                </div>
              )}
              <p style={{ marginTop: 10 }}>
                Since you already have an account, you can submit a new application without re-registering.
              </p>
              <button
                className="ip-pending-logout"
                style={{ marginTop: 16, background: '#CF142B', border: 'none', cursor: 'pointer' }}
                onClick={async () => {
                  try {
                    const res = await api.post('/instructor-auth/reapply-token');
                    const { registrationToken, email } = res;
                    sessionStorage.setItem('instructorRegToken', registrationToken);
                    sessionStorage.setItem('instructorPreEmail', email);
                    navigate('/apply');
                  } catch (err) {
                    alert('Unable to start re-application. Please contact support.');
                  }
                }}
                type="button"
              >
                Submit New Application →
              </button>
            </div>
          )}

          <p className="ip-pending-contact">
            Questions? Email us at{' '}
            <a href="mailto:contact@fasttrackds.co.uk">contact@fasttrackds.co.uk</a>
          </p>

          <button
            className="ip-pending-logout"
            onClick={handleLogout}
            type="button"
          >
            Sign Out
          </button>
        </div>
      </div>
    );
  }


  const displayName = instructor?.name || profile?.full_name || 'Instructor';
  const initials = displayName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div className="ip-layout">
      {/* ── SIDEBAR ── */}
      <aside className={`ip-sidebar ${mobileNavOpen ? 'ip-sidebar--open' : ''}`}>
        <div className="ip-sidebar-brand">
          <div className="ip-avatar">{initials}</div>
          <div className="ip-avatar-info">
            <span className="ip-avatar-name">{displayName}</span>
            <span className="ip-avatar-code">{instructor?.instructorCode || profile?.instructor_code}</span>
          </div>
        </div>

        <nav className="ip-nav">
          {TABS.map(tab => (
            <button
              key={tab.id}
              className={`ip-nav-item ${activeTab === tab.id ? 'ip-nav-item--active' : ''}`}
              onClick={() => { setActiveTab(tab.id); setMobileNavOpen(false); }}
              type="button"
            >
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>

        <button className="ip-logout-btn" onClick={handleLogout} type="button">
          Sign Out
        </button>
      </aside>

      {/* Mobile overlay */}
      {mobileNavOpen && (
        <div className="ip-overlay" onClick={() => setMobileNavOpen(false)} />
      )}

      {/* ── MAIN ── */}
      <div className="ip-main">
        {/* Mobile top bar */}
        <div className="ip-topbar">
          <button className="ip-topbar-menu" onClick={() => setMobileNavOpen(true)} type="button">
            ☰
          </button>
          <span className="ip-topbar-title">
            {TABS.find(t => t.id === activeTab)?.label}
          </span>
          <div className="ip-topbar-avatar">{initials}</div>
        </div>

        <div className="ip-content">

          {/* ═══════════════════════════════════════
              TAB: OVERVIEW
          ═══════════════════════════════════════ */}
          {activeTab === 'overview' && (
            <div className="ip-section">
              <h2 className="ip-section-title">Welcome back, {displayName.split(' ')[0]}!</h2>
              <p className="ip-section-sub">Here's a summary of your account.</p>

              <div className="ip-overview-cards">
                <div className="ip-ov-card">
                  <div className="ip-ov-icon">🪪</div>
                  <div className="ip-ov-body">
                    <span className="ip-ov-label">Instructor Code</span>
                    <span className="ip-ov-value">{profile?.instructor_code || '—'}</span>
                  </div>
                </div>
                <div className="ip-ov-card">
                  <div className="ip-ov-icon ip-ov-icon--ref">REF</div>
                  <div className="ip-ov-body">
                    <span className="ip-ov-label">Referral Code</span>
                    <span className="ip-ov-value">{profile?.referral_code || '—'}</span>
                  </div>
                </div>
                <div className="ip-ov-card">
                  <div className="ip-ov-icon">📅</div>
                  <div className="ip-ov-body">
                    <span className="ip-ov-label">Approved</span>
                    <span className="ip-ov-value">{fmtDate(profile?.approved_at)}</span>
                  </div>
                </div>
                <div className="ip-ov-card">
                  <div className="ip-ov-icon">{profile?.first_session_completed ? '✅' : '⏳'}</div>
                  <div className="ip-ov-body">
                    <span className="ip-ov-label">First Session</span>
                    <span className="ip-ov-value">{profile?.first_session_completed ? 'Completed' : 'Pending'}</span>
                  </div>
                </div>
              </div>

              {/* Vehicle summary */}
              <div className="ip-widget">
                <h3 className="ip-widget-title"> Vehicle on File</h3>
                {profile?.vehicle_make ? (
                  <div className="ip-kv-grid">
                    <div className="ip-kv"><span>Make / Model</span><strong>{profile.vehicle_make} {profile.vehicle_model}</strong></div>
                    <div className="ip-kv"><span>Registration</span><strong>{profile.vehicle_registration}</strong></div>
                    <div className="ip-kv"><span>Year</span><strong>{profile.vehicle_year}</strong></div>
                    <div className="ip-kv"><span>Colour</span><strong>{profile.vehicle_colour || '—'}</strong></div>
                    <div className="ip-kv"><span>Transmission</span><strong style={{ textTransform: 'capitalize' }}>{profile.transmission_type}</strong></div>
                  </div>
                ) : <p className="ip-empty">No vehicle on file.</p>}
              </div>

              {/* Document status summary */}
              <div className="ip-widget">
                <h3 className="ip-widget-title">Document Status</h3>
                <div className="ip-doc-summary">
                  {DOC_DEFINITIONS.map(doc => (
                    <div key={doc.type} className={`ip-doc-status-row ${hasFile(profile?.[doc.field]) ? 'ip-doc-status-row--ok' : 'ip-doc-status-row--missing'}`}>
                      <span className="ip-doc-status-icon">{hasFile(profile?.[doc.field]) ? '✅' : '⚠️'}</span>
                      <span className="ip-doc-status-label">{doc.label}</span>
                      {hasFile(profile?.[doc.field])
                        ? <span className="ip-doc-status-badge ip-badge--ok">On file</span>
                        : <span className="ip-doc-status-badge ip-badge--missing">Not uploaded</span>
                      }
                    </div>
                  ))}
                </div>
                <button
                  className="ip-btn ip-btn--secondary"
                  onClick={() => setActiveTab('documents')}
                  style={{ marginTop: '14px' }}
                  type="button"
                >
                  Manage Documents →
                </button>
              </div>
            </div>
          )}

          {/* ═══════════════════════════════════════
              TAB: PROFILE / MY DETAILS
          ═══════════════════════════════════════ */}
          {activeTab === 'profile' && (
            <div className="ip-section">
              <h2 className="ip-section-title">My Details</h2>
              <p className="ip-section-sub">Update your contact number and address.</p>

              {/* Read-only info */}
              <div className="ip-widget">
                <h3 className="ip-widget-title">Personal Information (Read Only)</h3>
                <div className="ip-kv-grid">
                  <div className="ip-kv"><span>Full Name</span><strong>{profile?.full_name}</strong></div>
                  <div className="ip-kv"><span>Email Address</span><strong>{profile?.email}</strong></div>
                  <div className="ip-kv"><span>Instructor Code</span><strong>{profile?.instructor_code}</strong></div>
                  <div className="ip-kv"><span>ADI PRN</span><strong>{profile?.prn || '—'}</strong></div>
                </div>
                <p className="ip-read-only-note">To update your name or email address, contact Fast Track admin.</p>
              </div>

              {/* Editable fields */}
              <div className="ip-widget">
                <h3 className="ip-widget-title">Editable Details</h3>
                {editMsg && (
                  <div className={`ip-alert ${editMsg.type === 'success' ? 'ip-alert--success' : 'ip-alert--error'}`}>
                    {editMsg.text}
                  </div>
                )}
                <form className="ip-form" onSubmit={handleSaveProfile}>
                  <div className="ip-form-row">
                    <div className="ip-field">
                      <label className="ip-label">Contact Number</label>
                      <input
                        className="ip-input"
                        type="tel"
                        value={editForm.contact_number || ''}
                        onChange={e => setEditForm(p => ({ ...p, contact_number: e.target.value }))}
                        placeholder="07xxx xxxxxx"
                      />
                    </div>
                  </div>
                  <div className="ip-field">
                    <label className="ip-label">Address Line 1</label>
                    <input
                      className="ip-input"
                      type="text"
                      value={editForm.address_line1 || ''}
                      onChange={e => setEditForm(p => ({ ...p, address_line1: e.target.value }))}
                      placeholder="House number and street name"
                    />
                  </div>
                  <div className="ip-field">
                    <label className="ip-label">Address Line 2 <span className="ip-optional">(optional)</span></label>
                    <input
                      className="ip-input"
                      type="text"
                      value={editForm.address_line2 || ''}
                      onChange={e => setEditForm(p => ({ ...p, address_line2: e.target.value }))}
                      placeholder="Flat / apartment / etc."
                    />
                  </div>
                  <div className="ip-form-row ip-form-row--2col">
                    <div className="ip-field">
                      <label className="ip-label">City / Town</label>
                      <input
                        className="ip-input"
                        type="text"
                        value={editForm.city || ''}
                        onChange={e => setEditForm(p => ({ ...p, city: e.target.value }))}
                        placeholder="e.g. London"
                      />
                    </div>
                    <div className="ip-field">
                      <label className="ip-label">Postcode</label>
                      <input
                        className="ip-input"
                        type="text"
                        value={editForm.postcode || ''}
                        onChange={e => setEditForm(p => ({ ...p, postcode: e.target.value }))}
                        placeholder="e.g. SW1A 1AA"
                        style={{ textTransform: 'uppercase' }}
                      />
                    </div>
                  </div>
                  <div className="ip-form-actions">
                    <button className="ip-btn ip-btn--primary" type="submit" disabled={editSaving}>
                      {editSaving ? <span className="ip-spinner" /> : '💾 Save Changes'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* ═══════════════════════════════════════
              TAB: DOCUMENTS
          ═══════════════════════════════════════ */}
          {activeTab === 'documents' && (
            <div className="ip-section">
              <h2 className="ip-section-title">Documents</h2>
              <p className="ip-section-sub">Re-upload expired or updated documents. Accepted formats: PDF, JPG, PNG (max 10 MB).</p>

              <div className="ip-doc-cards">
                {DOC_DEFINITIONS.map(doc => {
                  const uploadState = uploadStates[doc.type] || {};
                  const currentFile = profile?.[doc.field];

                  return (
                    <div key={doc.type} className="ip-doc-card">
                      <div className="ip-doc-card-header">
                        <span className="ip-doc-card-icon">{doc.icon}</span>
                        <div className="ip-doc-card-title-block">
                          <h3 className="ip-doc-card-title">{doc.label}</h3>
                          <p className="ip-doc-card-desc">{doc.description}</p>
                        </div>
                        <div className={`ip-doc-card-status ${hasFile(currentFile) ? 'ip-doc-card-status--ok' : 'ip-doc-card-status--missing'}`}>
                          {hasFile(currentFile) ? '✅ On file' : '⚠️ Missing'}
                        </div>
                      </div>

                      {uploadState.success && (
                        <div className="ip-alert ip-alert--success">{uploadState.success}</div>
                      )}
                      {uploadState.error && (
                        <div className="ip-alert ip-alert--error">{uploadState.error}</div>
                      )}

                      <div className="ip-doc-card-actions">
                        <input
                          type="file"
                          ref={el => fileInputRefs.current[doc.type] = el}
                          style={{ display: 'none' }}
                          accept=".pdf,.jpg,.jpeg,.png,.webp,.heic"
                          onChange={e => {
                            const file = e.target.files?.[0];
                            if (file) {
                              handleDocUpload(doc.type, file, {});
                            }
                            e.target.value = '';
                          }}
                        />
                        <button
                          className="ip-btn ip-btn--upload"
                          type="button"
                          disabled={uploadState.loading}
                          onClick={() => fileInputRefs.current[doc.type]?.click()}
                        >
                          {uploadState.loading
                            ? <><span className="ip-spinner" /> Uploading…</>
                            : hasFile(currentFile)
                              ? '🔄 Replace Document'
                              : '📤 Upload Document'
                          }
                        </button>
                        {hasFile(currentFile) && (
                          <a
                            className="ip-btn ip-btn--view-doc"
                            href={`/${currentFile}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            👁️ View Document
                          </a>
                        )}
                        {hasFile(currentFile) && (
                          <span className="ip-doc-filename">
                            📎 {currentFile.split('/').pop()}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ═══════════════════════════════════════
              TAB: TEST CENTRES
          ═══════════════════════════════════════ */}
          {activeTab === 'testcentres' && (
            <div className="ip-section">
              <h2 className="ip-section-title">Test Centres</h2>
              <p className="ip-section-sub">Manage the test centres where you teach.</p>

              {tcMsg && (
                <div className={`ip-alert ${tcMsg.type === 'success' ? 'ip-alert--success' : 'ip-alert--error'}`}>
                  {tcMsg.text}
                </div>
              )}

              {testCentres === null ? (
                <div className="ip-loading-inline"><span className="ip-spinner" /> Loading…</div>
              ) : (() => {
                const registered = testCentres.filter(c => c.selected);
                const q = tcSearch.trim().toLowerCase();
                const unregistered = testCentres.filter(c => !c.selected);
                const filtered = q
                  ? unregistered.filter(c =>
                      c.name.toLowerCase().includes(q) ||
                      (c.city || '').toLowerCase().includes(q) ||
                      (c.region || '').toLowerCase().includes(q)
                    )
                  : unregistered;
                const regions = [...new Set(filtered.map(c => c.region || 'Other'))].sort();

                return (
                  <>
                    {/* ═══ SECTION 1: Registered centres ═══ */}
                    <div className="ip-widget">
                      <h3 className="ip-widget-title">Registered Centres
                        {registered.length > 0 && (
                          <span className="ip-tc-counter" style={{ marginLeft: 10 }}>{registered.length}</span>
                        )}
                      </h3>

                      {registered.length === 0 ? (
                        <p className="ip-empty" style={{ margin: 0 }}>You are not registered at any test centre yet. Add centres below.</p>
                      ) : (
                        <div className="ip-tc-registered-list">
                          {registered.map(centre => (
                            <div key={centre.id} className="ip-tc-registered-item">
                              <div className="ip-tc-registered-info">
                                <span className="ip-tc-name">{centre.name}</span>
                                <span className="ip-tc-city">{centre.city}{centre.postcode ? `, ${centre.postcode}` : ''}</span>
                                {centre.region && <span className="ip-tc-region-tag">{centre.region}</span>}
                              </div>
                              <button
                                className="ip-tc-remove-btn"
                                type="button"
                                onClick={() => handleRemoveTestCentre(centre.id)}
                                disabled={tcDeleting === centre.id}
                                title={`Remove ${centre.name}`}
                              >
                                {tcDeleting === centre.id
                                  ? <span className="ip-spinner ip-spinner--sm" />
                                  : <>✕ Remove</>}
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* ═══ SECTION 2: Add more centres ═══ */}
                    <div className="ip-widget ip-widget--tc-add" style={{ marginTop: 20 }}>
                      <h3 className="ip-widget-title">Add Test Centres</h3>

                      {/* Toolbar */}
                      <div className="ip-tc-toolbar">
                        <div className="ip-tc-search-wrap">
                          <span className="ip-tc-search-icon">🔍</span>
                          <input
                            className="ip-tc-search"
                            type="text"
                            placeholder="Search by name, city or region…"
                            value={tcSearch}
                            onChange={e => setTcSearch(e.target.value)}
                          />
                          {tcSearch && (
                            <button
                              className="ip-tc-search-clear"
                              type="button"
                              onClick={() => setTcSearch('')}
                              aria-label="Clear search"
                            >✕</button>
                          )}
                        </div>
                        <div className="ip-tc-toolbar-right">
                          {selectedCentreIds.size > registered.length && (
                            <span className="ip-tc-counter">
                              +{selectedCentreIds.size - registered.length} to add
                            </span>
                          )}
                          <button
                            className="ip-btn ip-btn--primary ip-tc-save-btn"
                            type="button"
                            onClick={handleSaveTestCentres}
                            disabled={tcSaving || selectedCentreIds.size === registered.length}
                          >
                            {tcSaving ? <><span className="ip-spinner" /> Saving…</> : '💾 Save'}
                          </button>
                          <button
                            className="ip-btn ip-btn--ghost"
                            type="button"
                            onClick={() => { setTestCentres(null); setTcSearch(''); setTcMsg(null); }}
                            title="Refresh list"
                          >↺</button>
                        </div>
                      </div>

                      {/* Grid */}
                      {unregistered.length === 0 ? (
                        <p className="ip-empty" style={{ margin: '12px 0 0' }}>You are registered at all available test centres.</p>
                      ) : filtered.length === 0 ? (
                        <div className="ip-empty-state" style={{ marginTop: 12 }}>
                          <p>No test centres match &quot;{tcSearch}&quot;.</p>
                          <button className="ip-btn ip-btn--secondary" type="button" onClick={() => setTcSearch('')}>Clear search</button>
                        </div>
                      ) : (
                        <div className="ip-tc-scroll-body">
                          {regions.map(region => (
                          <div key={region} className="ip-tc-region">
                            <h4 className="ip-tc-region-title">
                              {region}
                              <span className="ip-tc-region-count">
                                {filtered.filter(c => (c.region || 'Other') === region && selectedCentreIds.has(c.id)).length}
                                /{filtered.filter(c => (c.region || 'Other') === region).length}
                              </span>
                            </h4>
                            <div className="ip-tc-grid">
                              {filtered.filter(c => (c.region || 'Other') === region).map(centre => (
                                <label
                                  key={centre.id}
                                  className={`ip-tc-item${selectedCentreIds.has(centre.id) ? ' ip-tc-item--checked' : ''}`}
                                >
                                  <input
                                    type="checkbox"
                                    className="ip-tc-checkbox"
                                    checked={selectedCentreIds.has(centre.id)}
                                    onChange={e => {
                                      setSelectedCentreIds(prev => {
                                        const next = new Set(prev);
                                        if (e.target.checked) next.add(centre.id);
                                        else next.delete(centre.id);
                                        return next;
                                      });
                                      setTcMsg(null);
                                    }}
                                  />
                                  <div className="ip-tc-info">
                                    <span className="ip-tc-name">{centre.name}</span>
                                    <span className="ip-tc-city">{centre.city}{centre.postcode ? `, ${centre.postcode}` : ''}</span>
                                  </div>
                                  {selectedCentreIds.has(centre.id) && (
                                    <span className="ip-tc-tick">✓</span>
                                  )}
                                </label>
                              ))}
                            </div>
                          </div>
                        ))
                        }
                        </div>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
          )}

          {/* ═══════════════════════════════════════
              TAB: REFERRALS
          ═══════════════════════════════════════ */}
          {activeTab === 'referrals' && (
            <div className="ip-section">
              <h2 className="ip-section-title">My Referrals</h2>
              <p className="ip-section-sub">
                Your referral code: <strong className="ip-highlight">{profile?.referral_code}</strong>
                {' '}— share it with prospective instructors.
              </p>

              {referralStats && (
                <div className="ip-ref-stats">
                  <div className="ip-ref-stat">
                    <span className="ip-ref-stat-num">{referralStats.total}</span>
                    <span className="ip-ref-stat-label">Total</span>
                  </div>
                  <div className="ip-ref-stat">
                    <span className="ip-ref-stat-num">{referralStats.pending}</span>
                    <span className="ip-ref-stat-label">Pending</span>
                  </div>
                  <div className="ip-ref-stat">
                    <span className="ip-ref-stat-num">{referralStats.approved}</span>
                    <span className="ip-ref-stat-label">Approved</span>
                  </div>
                  <div className="ip-ref-stat">
                    <span className="ip-ref-stat-num">£{referralStats.totalBonus?.toFixed(2)}</span>
                    <span className="ip-ref-stat-label">Total Bonus</span>
                  </div>
                </div>
              )}

              {referrals === null ? (
                <div className="ip-loading-inline"><span className="ip-spinner" /> Loading…</div>
              ) : referrals.length === 0 ? (
                <div className="ip-empty-state">
                  <p>No referrals yet. Share your code to start earning!</p>
                </div>
              ) : (
                <div className="ip-table-wrap">
                  <table className="ip-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Referred Person</th>
                        <th>Email</th>
                        <th>Status</th>
                        <th>Bonus</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {referrals.map((r, i) => (
                        <tr key={r.id}>
                          <td>{i + 1}</td>
                          <td>{r.referred_name}</td>
                          <td className="ip-td-muted">{r.referred_email}</td>
                          <td>
                            <span className={`ip-badge ip-badge--${r.status}`}>
                              {r.status.charAt(0).toUpperCase() + r.status.slice(1)}
                            </span>
                          </td>
                          <td>£{parseFloat(r.bonus_amount || 0).toFixed(2)}</td>
                          <td className="ip-td-muted">{fmtDate(r.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ═══════════════════════════════════════
              TAB: BOOKINGS
          ═══════════════════════════════════════ */}
          {activeTab === 'bookings' && (
            <div className="ip-section">
              <h2 className="ip-section-title">Student Bookings</h2>
              <p className="ip-section-sub">Students booked with you. Contact them to arrange lesson times.</p>

              {bookingsLoading ? (
                <div className="ip-loading-inline"><span className="ip-spinner" /> Loading bookings…</div>
              ) : !bookings || bookings.length === 0 ? (
                <div className="ip-empty-state">

                  <p>No bookings yet. Once students select you during booking, they’ll appear here.</p>
                </div>
              ) : (
                <>
                  <p className="ip-section-sub" style={{ marginBottom: '16px' }}>
                    <strong>{bookings.length}</strong> booking{bookings.length !== 1 ? 's' : ''} received
                  </p>
                  <div className="ip-bookings-list">
                    {bookings.map(b => (
                      <div key={b.id} className={`ip-booking-card ip-booking-card--${b.status}`}>
                        <div className="ip-booking-header">
                          <span className="ip-booking-name">{b.full_name}</span>
                          <span className={`ip-badge ip-badge--${b.status}`}>
                            {b.status?.charAt(0).toUpperCase() + b.status?.slice(1)}
                          </span>
                        </div>
                        {/* Contact details prominently shown */}
                        <div className="ip-booking-contact">
                          <a href={`tel:${b.phone}`} className="ip-booking-contact-item ip-booking-contact-item--phone">
                            <strong>{b.phone || 'No phone'}</strong>
                          </a>
                          <a href={`mailto:${b.email}`} className="ip-booking-contact-item ip-booking-contact-item--email">
                            {b.email}
                          </a>
                        </div>
                        <div className="ip-booking-meta">
                          {b.test_centre && <span>Area: {b.test_centre}</span>}
                          {b.transmission && <span>Transmission: {b.transmission}</span>}
                          {b.package_type && <span>Package: {b.package_type}</span>}
                          <span>Booked: {fmtDate(b.created_at)}</span>
                        </div>
                        {b.message && (
                          <div className="ip-booking-message">
                            <span>“{b.message}”</span>
                          </div>
                        )}                        {/* Mark session complete — only for active bookings */}
                        {(b.status === 'approved' || b.status === 'assigned') && (
                          <div className="ip-booking-actions">
                            <button
                              className="ip-btn ip-btn--complete"
                              onClick={() => handleMarkComplete(b.id)}
                              disabled={completingId === b.id}
                              type="button"
                            >
                              {completingId === b.id ? 'Marking…' : 'Mark Session Done'}
                            </button>
                          </div>
                        )}
                        {b.status === 'completed' && (
                          <div className="ip-booking-done-badge">
                            Session completed
                          </div>
                        )}                      </div>
                    ))}
                  </div>
                </>
              )}

              <button
                className="ip-btn ip-btn--secondary"
                onClick={() => { setBookings(null); }}
                style={{ marginTop: '16px' }}
                type="button"
              >
                Refresh Bookings
              </button>
            </div>
          )}

          {/* ═══════════════════════════════════════
              TAB: SECURITY
          ═══════════════════════════════════════ */}
          {activeTab === 'security' && (
            <div className="ip-section">
              <h2 className="ip-section-title">Security</h2>
              <p className="ip-section-sub">Change your portal password.</p>

              <div className="ip-widget ip-widget--narrow">
                {pwMsg && (
                  <div className={`ip-alert ${pwMsg.type === 'success' ? 'ip-alert--success' : 'ip-alert--error'}`}>
                    {pwMsg.text}
                  </div>
                )}
                <form className="ip-form" onSubmit={handleChangePassword}>
                  <div className="ip-field">
                    <label className="ip-label">Current Password</label>
                    <input
                      className="ip-input"
                      type="password"
                      value={pwForm.current_password}
                      onChange={e => setPwForm(p => ({ ...p, current_password: e.target.value }))}
                      required
                      placeholder="Enter current password"
                    />
                  </div>
                  <div className="ip-field">
                    <label className="ip-label">New Password</label>
                    <input
                      className="ip-input"
                      type="password"
                      value={pwForm.new_password}
                      onChange={e => setPwForm(p => ({ ...p, new_password: e.target.value }))}
                      required
                      placeholder="Minimum 8 characters"
                    />
                  </div>
                  <div className="ip-field">
                    <label className="ip-label">Confirm New Password</label>
                    <input
                      className="ip-input"
                      type="password"
                      value={pwForm.confirm_password}
                      onChange={e => setPwForm(p => ({ ...p, confirm_password: e.target.value }))}
                      required
                      placeholder="Repeat new password"
                    />
                  </div>
                  <div className="ip-form-actions">
                    <button className="ip-btn ip-btn--primary" type="submit" disabled={pwSaving}>
                      {pwSaving ? <span className="ip-spinner" /> : 'Change Password'}
                    </button>
                  </div>
                </form>
              </div>

              <div className="ip-widget ip-widget--narrow" style={{ marginTop: '20px' }}>
                <h3 className="ip-widget-title">Account Info</h3>
                <div className="ip-kv-grid">
                  <div className="ip-kv"><span>Account Email</span><strong>{profile?.email}</strong></div>
                  <div className="ip-kv"><span>Last Login</span><strong>{fmtDate(profile?.last_login)}</strong></div>
                  <div className="ip-kv"><span>Account Status</span>
                    <strong>
                      <span className={`ip-badge ${profile?.is_active ? 'ip-badge--approved' : 'ip-badge--rejected'}`}>
                        {profile?.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </strong>
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>{/* /ip-content */}
      </div>{/* /ip-main */}
    </div>
  );
};

export default InstructorPortal;
